import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import {
  getUrlParamsFetch,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import {
  AxiosCodeEnum,
  TipoContribuyente,
  TiposContribuyentePagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';

import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchTipoContribuyentes = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetTipoContribuyentesParams>) => {
  return useQuery({
    queryKey: ['tipo-contribuyentes', ...Object.values(params ?? {})],
    queryFn: () => getTipoContribuyentes(params),
    enabled: enabled,
  });
};

export const useGetTipoContribuyente = (id: string) => {
  return useQuery({
    queryKey: ['tipo-contribuyente', id],
    queryFn: () => getTipoContribuyente(id),
    retry: false,
  });
};

export const useCreateTipoContribuyente = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTipoContribuyente,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['tipo-contribuyentes'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      if (res.code === AxiosCodeEnum.CREATED) {
        setAlert(true, res.message, res.status);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTipoContribuyente = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTipoContribuyente,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['tipo-contribuyentes'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      if (res.code === AxiosCodeEnum.SUCCESS) {
        setAlert(true, res.message, res.status);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteTipoContribuyente = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deleteTipoContribuyente,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['tipo-contribuyentes'] });
      if (res.code === AxiosCodeEnum.SUCCESS) {
        setAlert(true, res.message, res.status);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetTipoContribuyentesParams = Partial<TipoContribuyente> & {
  page?: number;
  page_size?: number;

  // filter by state
  filterByState?: boolean;
};

export type CreateTipoContribuyenteParams = Omit<
  TipoContribuyente,
  'id_tipo_contribuyente'
>;
export type UpdateTipoContribuyenteParams = {
  id: string;
  data: CreateTipoContribuyenteParams;
};

export const getTipoContribuyentes = async (
  params?: GetTipoContribuyentesParams
) => {
  // filter by state
  const stateParams = { ...params };
  if (stateParams.filterByState === false) {
    delete stateParams.estado;
  } else {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TiposContribuyentePagingRes>(
    `${urlBackend}api/v1/tipos-contribuyentes/?${queryParams}`,
    true
  );
};

export const getTipoContribuyente = async (id: string) => {
  const res = await get<TipoContribuyente>(
    `${urlBackend}api/v1/tipos-contribuyentes/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createTipoContribuyente = async (
  data: CreateTipoContribuyenteParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await post<TipoContribuyente>(
    `${urlBackend}api/v1/tipos-contribuyentes/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoContribuyente = async ({
  id,
  data,
}: UpdateTipoContribuyenteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await put<TipoContribuyente>(
    `${urlBackend}api/v1/tipos-contribuyentes/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteTipoContribuyente = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await remove<TipoContribuyente>(
    `${urlBackend}api/v1/tipos-contribuyentes/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
